import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Container } from "./styles";
import { Link, useParams, useHistory, useLocation } from "react-router-dom";
import { sendAppEvent } from "../../Utils";
import { Spinner } from "src/sudokit/components/Spinner";
import classNames from "classnames";
import PluginBox from "../PluginBox";
import PageNotFound from "../../PageNotFound";
import FeaturedPlugins from "../FeaturedPlugins";
import { useAppStore } from "../../stores/appStore";
import usePluginsCategories from "./hooks/usePluginCategories";
import usePlugins from "./hooks/usePlugins";
import useInstalledPlugins from "./hooks/useInstalledPlugins";
import usePublicMode from "../../hooks/usePublicMode";
import AssetSvg from "../AssetSvg";

const SORT_TYPES = {
  POPULAR: "Popular",
  NEWEST: "Newest",
  ADDED: "Added",
  YOURS: "Yours",
};

// List has the following facets:
// - sort: popular, newest
// - filter: category, own
// - installed: bool
// - searchQuery: str

const perPage = 15;

export default function Plugins({ openLogin }) {
  const { query, categorySlug } = useParams();
  const history = useHistory();
  const location = useLocation();

  const user = useAppStore((state) => state.user);

  const { pluginCategories, isLoading: categoriesLoading, error: categoriesError } = usePluginsCategories({ includeOther: false });

  const selectedCategory = useMemo(() => (pluginCategories && categorySlug ? pluginCategories.find((category) => category.slug === categorySlug) : null), [categorySlug, pluginCategories]);

  const [sort, setSort] = useState(SORT_TYPES.POPULAR);
  const [filter, setFilter] = useState({
    own: false,
  });
  const [searchInput, setSearchInput] = useState("");
  const [showInstalledPlugins, setShowInstalledPlugins] = useState(false);
  const fetchBoundaryRef = useRef(null);

  const {
    plugins,
    error: pluginsError,
    isLoading: pluginsLoading,
    isValidating,
    setPagesRequested,
    haveMore,
  } = usePlugins({
    sort,
    filter: {
      ...filter,
      categoryId: selectedCategory?.id,
    },
    searchQuery: query,
    perPage,
  });

  const { plugins: installedPlugins, loading: installedPluginsLoading, error: installedPluginsError } = useInstalledPlugins({ user, category: selectedCategory });

  const notFound = categorySlug && !categoriesLoading && !categoriesError && !selectedCategory;
  const isSearch = query?.length > 0;
  const isLoading = categoriesLoading || pluginsLoading || installedPluginsLoading;
  const showFeatured = !isSearch;
  const isError = pluginsError || installedPluginsError;
  const { urlPrefixPlugins } = usePublicMode();

  const resetSearch = useCallback(() => {
    history.push(urlPrefixPlugins);
    setSearchInput("");
  }, [history, urlPrefixPlugins]);

  // Search
  useEffect(() => {
    if (query?.length) {
      setSearchInput(query);
    } else {
      if (categorySlug) {
        setSearchInput("");
      } else {
        // Only clear search input without pushing a new history entry.
        // Using history.push here would strip location.state (e.g. the
        // origin-aware back-navigation "from" field) on initial mount.
        setSearchInput("");
      }
    }
  }, [categorySlug, query]);

  // Analytics
  useEffect(() => {
    sendAppEvent("Plugins - Index Page View", { sort });
  }, [sort]);

  const isSortTypeActive = (sortType) => {
    if (isSearch) {
      return false;
    }

    switch (sortType) {
      case SORT_TYPES.YOURS:
        return filter.own;
      case SORT_TYPES.ADDED:
        return showInstalledPlugins;
      default:
        return sort === sortType;
    }
  };

  const createSortSelectHandler = (value) => () => {
    if (isSearch) {
      history.push(urlPrefixPlugins);
      setSearchInput("");
    }

    if (value === SORT_TYPES.ADDED) {
      setShowInstalledPlugins(true);
      setSort(null);
      setFilter({
        ...filter,
        own: false,
      });
    } else if (value === SORT_TYPES.YOURS) {
      setShowInstalledPlugins(false);
      setSort(null);
      setFilter({
        ...filter,
        own: true,
      });
    } else {
      setShowInstalledPlugins(false);
      setSort(value);
      setFilter({
        ...filter,
        own: false,
      });
    }
  };

  const observerCallback = useCallback(
    (entries) => {
      const trigger = entries[0].isIntersecting;
      const shouldLoadMore = trigger && plugins?.length && haveMore && !isValidating;
      if (shouldLoadMore) {
        setPagesRequested((pages) => pages + 1);
      }
    },
    [haveMore, isValidating, plugins?.length, setPagesRequested]
  );

  useEffect(() => {
    if (!fetchBoundaryRef.current) {
      return;
    }

    if (!plugins) {
      return;
    }

    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: "50px",
      threshold: 1,
    });

    observer.observe(fetchBoundaryRef.current);

    return () => {
      observer.disconnect();
    };
  }, [observerCallback, plugins]);

  const displayableSortTypes = user ? Object.values(SORT_TYPES) : [SORT_TYPES.POPULAR, SORT_TYPES.NEWEST];

  let pluginsToDisplay = plugins;
  if (showInstalledPlugins) {
    pluginsToDisplay = installedPlugins;
  }

  const isEmpty = !isLoading && pluginsToDisplay?.length === 0;
  let emptyMessage = "";
  if (isEmpty) {
    if (isSearch) {
      emptyMessage = (
        <>
          No plugins matched your search.
          <a onClick={resetSearch}> Reset your search.</a>
        </>
      );
    } else if (filter.own) {
      if (categorySlug) {
        emptyMessage = (
          <>
            <p>You don't have any plugins in category yet.</p>
            <Link to={`${urlPrefixPlugins}/new`}>Create one.</Link>
          </>
        );
      } else {
        emptyMessage = (
          <>
            <p>You haven't created any plugins yet.</p>
            <Link to={`${urlPrefixPlugins}/new`}>Create one.</Link>
          </>
        );
      }
    } else if (showInstalledPlugins) {
      if (categorySlug) {
        emptyMessage = <>You haven't added any plugins in this category yet.</>;
      } else {
        emptyMessage = <>You haven't added any plugins yet.</>;
      }
    } else {
      // we should never get here in prod
      if (categorySlug) {
        emptyMessage = <>There are no plugins in this category yet.</>;
      } else {
        emptyMessage = <>There are no plugins yet.</>;
      }
    }
  }

  if (notFound) {
    return <PageNotFound />;
  }

  const showSpinner = !showInstalledPlugins && isValidating;

  return (
    <Container>
      <div className="categories">
        {user && (
          <Link to={location.state?.from || "/app"} className="back-button">
            <img src="/assets/back-arrow.svg" alt="" />
            <span>Back</span>
          </Link>
        )}
        <ul>
          <li key="all">
            <Link
              to={`${urlPrefixPlugins}/`}
              className={classNames({ selected: !categorySlug && !isSearch })}
            >
              <div className="vertical-selected-indicator"></div>
              <p>All Plugins</p>
            </Link>
          </li>
          {pluginCategories?.map((category) => (
            <li key={category.id}>
              <Link
                to={`${urlPrefixPlugins}/category/${category.slug}`}
                className={classNames({ selected: categorySlug === category.slug && !isSearch })}
              >
                <div className="vertical-selected-indicator"></div>
                <p>{category.name}</p>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="plugin-list-container">
        <div>
          {showFeatured && (
            <FeaturedPlugins
              pluginCategory={selectedCategory}
              user={user}
            />
          )}
        </div>
        <div className="header">
          <div></div>

          <div className="sort-toggle">
            {displayableSortTypes.map((value) => (
              <a
                className={classNames({ selected: isSortTypeActive(value) })}
                key={value}
                onClick={createSortSelectHandler(value)}
              >
                <p>{value}</p>
                <div className="horizontal-selected-indicator"></div>
              </a>
            ))}
          </div>
          <div className="search-container">
            <form
              className="search"
              onSubmit={(e) => {
                e.preventDefault();

                if (searchInput.length > 0) {
                  history.push(`${urlPrefixPlugins}/search/${searchInput}`);
                } else {
                  resetSearch();
                }
              }}
            >
              <AssetSvg
                src="search.svg"
                height={26}
                width={26}
                fill="var(--grey-1)"
              />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search Plugins"
              />
            </form>

            {user && (
              <Link to="/app/my-plugin/new">
                <button>+ Create Plugin</button>
              </Link>
            )}
          </div>
        </div>
        {isError ? (
          <div>An error occurred. Please try after a while</div>
        ) : isLoading ? (
          isSearch ? (
            <p className="none">Searching...</p>
          ) : (
            <div className="loading">Loading...</div>
          )
        ) : (
          <>
            {isSearch && !isLoading && pluginsToDisplay?.length > 0 && (
              <div className="search-header">
                <span>Results for "{query}"</span>. <a onClick={resetSearch}>Reset your search</a>
              </div>
            )}

            <div className="main-container">
              <div className="plugins-container">
                {emptyMessage && <p className="none">{emptyMessage}</p>}

                <div className="plugins">
                  {pluginsToDisplay?.map?.((plugin) => (
                    <PluginBox
                      key={plugin.shortId}
                      plugin={plugin}
                      openLogin={openLogin}
                    />
                  ))}
                </div>
                {Boolean(pluginsToDisplay?.length) && <div ref={fetchBoundaryRef}>&nbsp;</div>}
                {showSpinner && (
                  <div className="spinner-container">
                    <Spinner size="md" />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Container>
  );
}
